import React, { useState } from 'react';
import { X } from 'lucide-react';
import { capacityLimitsService } from 'api/services/capacityLimits.service';
import { directionOf, reasonError, NOT_YET_ENFORCED_COPY } from '../capacityEnforcementRules';

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

const parse = (v: string): number | null => (v.trim() === '' ? null : Number(v));

/**
 * Increase / decrease a trunk's channels or CPS (master only). Blank = unlimited.
 * A reason is required; the change is recorded in the capacity history, not pushed.
 */
const TrunkCapacityModal: React.FC<{
  providerId: number; name: string; cps: number | null; maxChannels: number | null;
  onClose: () => void; onSaved: () => void;
}> = ({ providerId, name, cps, maxChannels, onClose, onSaved }) => {
  const [newCh, setNewCh] = useState(maxChannels == null ? '' : String(maxChannels));
  const [newCps, setNewCps] = useState(cps == null ? '' : String(cps));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const after = { cps: parse(newCps), ch: parse(newCh) };
  const direction = directionOf({ cps, ch: maxChannels }, after);

  const save = async () => {
    setError(null);
    if ((after.ch !== null && (!Number.isInteger(after.ch) || after.ch <= 0))
      || (after.cps !== null && (!Number.isInteger(after.cps) || after.cps <= 0))) {
      setError('Figures must be positive whole numbers; leave blank for unlimited.');
      return;
    }
    if (!direction) { setError('Nothing to change.'); return; }
    const rErr = reasonError(reason);
    if (rErr) { setError(rErr); return; }
    setSaving(true);
    const res = await capacityLimitsService.changeTrunk(providerId, {
      cps: after.cps, maxChannels: after.ch,
      clearCps: after.cps === null && cps !== null, clearMaxChannels: after.ch === null && maxChannels !== null,
      reason: reason.trim(),
    });
    setSaving(false);
    if (res.success) onSaved(); else setError(res.error || 'Change refused');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change trunk capacity — {name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Channels (now {maxChannels ?? '∞'})</span>
              <input type="number" min={1} value={newCh} onChange={e => setNewCh(e.target.value)} className={INPUT} placeholder="unlimited" />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-gray-700 dark:text-gray-300 mb-1">CPS (now {cps ?? '∞'})</span>
              <input type="number" min={1} value={newCps} onChange={e => setNewCps(e.target.value)} className={INPUT} placeholder="unlimited" />
            </label>
          </div>
          {direction && <p className="text-sm text-gray-700 dark:text-gray-300">This is a <b>{direction}</b>.</p>}
          <label className="block text-sm">
            <span className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (required)</span>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className={INPUT}
              placeholder="e.g. Tata contract: 40 channels on the mobile trunk" />
          </label>
          <p className="text-xs text-amber-700 dark:text-amber-400">{NOT_YET_ENFORCED_COPY} cpass's own throttle uses it for calls cpass places; the switch's limiter is unchanged.</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">Record change</button>
        </div>
      </div>
    </div>
  );
};

export default TrunkCapacityModal;
